import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Upload, 
  Sparkles,
  ArrowRight,
  BookOpen,
  X,
} from 'lucide-react';
import { cn } from '../utils/cn';

interface CreationMethod {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  gradient: string;
  features: string[];
  route: string;
}

interface CreateContentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateContentModal: React.FC<CreateContentModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  const methods: CreationMethod[] = [
    {
      id: 'manual',
      title: 'Manual Creation',
      description: 'Build your tutorial step by step with full control',
      icon: FileText,
      color: 'text-blue-600',
      gradient: 'from-blue-500 to-blue-600',
      features: [
        'Complete control over structure',
        'Add text, video, code, quizzes',
        'Upload images and media',
        'Perfect for custom content'
      ],
      route: '/builder'
    },
    {
      id: 'pdf-import',
      title: 'PDF Import',
      description: 'Upload a PDF and automatically convert it to a tutorial',
      icon: Upload,
      color: 'text-orange-600',
      gradient: 'from-orange-500 to-orange-600',
      features: [
        'Automatic content extraction',
        'Image extraction and placement',
        'AI-powered structure generation',
        'Edit and refine after import'
      ],
      route: '/builder?method=pdf'
    },
    {
      id: 'ai-assisted',
      title: 'AI-Assisted Creation',
      description: 'Chat with AI to create tutorials with intelligent assistance',
      icon: Sparkles,
      color: 'text-purple-600',
      gradient: 'from-purple-500 to-purple-600',
      features: [
        'Conversational content creation',
        'Voice and text input',
        'AI-powered media placement',
        'Live editing with feedback loop'
      ],
      route: '/builder/ai-assisted'
    }
  ];

  const handleMethodSelect = (method: CreationMethod) => {
    onClose();
    navigate(method.route);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black bg-opacity-50 backdrop-blur-sm"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto pointer-events-auto"
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Create New Content
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Choose how you'd like to create your tutorial or learning content
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Method Selection Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {methods.map((method, index) => {
                    const Icon = method.icon;
                    return (
                      <motion.div
                        key={method.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className="relative group"
                      >
                        <div
                          onClick={() => handleMethodSelect(method)}
                          className={cn(
                            "relative h-full bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden",
                            "border-2 border-gray-200 hover:border-gray-300",
                            "transform hover:-translate-y-1"
                          )}
                        >
                          {/* Gradient Background */}
                          <div className={cn(
                            "absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-5 transition-opacity duration-300",
                            `bg-gradient-to-br ${method.gradient}`
                          )} />

                          {/* Content */}
                          <div className="relative p-6">
                            {/* Icon */}
                            <div className={cn(
                              "w-20 h-20 rounded-2xl bg-gradient-to-br flex items-center justify-center mb-6 shadow-lg",
                              `bg-gradient-to-br ${method.gradient}`
                            )}>
                              <Icon className="w-10 h-10 text-white" />
                            </div>

                            {/* Title */}
                            <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-800 transition-colors">
                              {method.title}
                            </h3>

                            {/* Description */}
                            <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                              {method.description}
                            </p>

                            {/* Features */}
                            <ul className="space-y-2 mb-6">
                              {method.features.map((feature, idx) => (
                                <li key={idx} className="flex items-start text-xs text-gray-700">
                                  <ArrowRight className={cn("w-3.5 h-3.5 mr-2 mt-0.5 flex-shrink-0", method.color)} />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>

                            {/* Action Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMethodSelect(method);
                              }}
                              className={cn(
                                "w-full flex items-center justify-center space-x-3 px-6 py-4 rounded-xl bg-gradient-to-r text-white font-semibold text-lg transition-all duration-300 shadow-lg",
                                `bg-gradient-to-r ${method.gradient}`,
                                "group-hover:scale-105 group-hover:shadow-2xl",
                                "hover:scale-105 active:scale-100",
                                "hover:shadow-xl"
                              )}
                              style={{
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 0 20px -5px rgba(99, 102, 241, 0.2)'
                              }}
                            >
                              <span>Get Started</span>
                              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                          </div>

                          {/* Hover Effect */}
                          <div className={cn(
                            "absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity duration-300",
                            `bg-gradient-to-br ${method.gradient}`
                          )} />
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Additional Info */}
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Need Help Choosing?
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-gray-700">
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center text-sm">
                        <BookOpen className="w-4 h-4 mr-2 text-blue-600" />
                        Manual Creation
                      </h4>
                      <p className="text-xs">
                        Best for when you know exactly what you want and prefer full control over every detail.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center text-sm">
                        <Upload className="w-4 h-4 mr-2 text-orange-600" />
                        PDF Import
                      </h4>
                      <p className="text-xs">
                        Perfect when you have existing documentation in PDF format that you want to convert to interactive tutorials.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center text-sm">
                        <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
                        AI-Assisted
                      </h4>
                      <p className="text-xs">
                        Ideal for collaborative content creation with AI guidance, especially when you want to explore ideas and refine content iteratively.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreateContentModal;

